/**
 * Shared utility functions
 */

import { UserRole } from '../types/user.types';

/**
 * Case-insensitive, trimmed membership check for free-text department values.
 * Department is a free-text field (see user.types.ts), not an enum, so exact
 * string matching would be fragile against casing/whitespace differences.
 */
export const matchesAnyDepartment = (
  department: string | undefined | null,
  allowedDepartments: string[]
): boolean => {
  if (!department) return false;
  const normalized = department.trim().toLowerCase();
  return allowedDepartments.some((d) => d.trim().toLowerCase() === normalized);
};

/**
 * Combined role-or-department access check: a user passes if their role is
 * in allowedRoles, OR they are an employee whose department is in
 * allowedDepartments. Used to grant department-based modules (e.g. HR /
 * Talent Acquisition access to Onboarding) without introducing new roles.
 */
export const hasRoleOrDepartmentAccess = (
  role: string | undefined,
  department: string | undefined | null,
  allowedRoles?: string[],
  allowedDepartments?: string[]
): boolean => {
  if (!allowedRoles && !allowedDepartments) return true;
  if (allowedRoles && role && allowedRoles.includes(role)) return true;
  if (
    allowedDepartments &&
    role === UserRole.EMPLOYEE &&
    matchesAnyDepartment(department, allowedDepartments)
  ) {
    return true;
  }
  return false;
};

/**
 * Validates email format
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validates phone number format (supports various formats)
 */
export const isValidPhone = (phone: string): boolean => {
  const phoneRegex = /^[\d\s\-+()]+$/;
  return phoneRegex.test(phone) && phone.replace(/\D/g, '').length >= 10;
};

/**
 * Validates password strength
 */
export const isStrongPassword = (password: string): boolean => {
  return (
    password.length >= 8 &&
    /[A-Z]/.test(password) &&
    /[a-z]/.test(password) &&
    /[0-9]/.test(password) &&
    /[!@#$%^&*(),.?":{}|<>]/.test(password)
  );
};

/**
 * Formats date to readable string
 */
export const formatDate = (date: Date | string): string => {
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * Generates a random string
 */
export const generateRandomString = (length: number): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};
